// shared-lib/geometry/wallOutlineEnds.js
'use strict';

/**
 * The two ENDS of a wall, read off its `polygonCorners` outline.
 *
 * The outline is drawn in the wall's own vertical plane, so seen from above
 * every corner lies on the wall line, and the ends are the two corners
 * farthest apart in plan. Nothing about the outline's SHAPE may be assumed:
 *
 *   - a structure wall gets a corner wherever a room wall starts or ends
 *     along it (processing Step 3.6c) — 12 along the bottom of one 8 m
 *     kitchen wall, all at one height;
 *   - a room wall can step, at its top or its bottom (a sunken room on one
 *     side, a soffit, a gable).
 *
 * The rule this replaces — "the two lowest corners", or `corners[0]` /
 * `corners[1]` — is only right for a plain rectangle. On the kitchen wall
 * above it returned a 0.52 m stub of an 8.1 m wall, so nothing could be
 * placed on the rest of it.
 *
 * Each end is the LOWEST corner standing there (within `END_TOLERANCE_M` in
 * plan), so a caller measuring in 3D gets the foot of the wall at that end,
 * not its top. The pair is returned in outline order: on a plain
 * `[BL, BR, TR, TL]` rectangle that is `[BL, BR]`, exactly `corners[0]` /
 * `corners[1]`, so a caller that relied on the face's left-to-right order
 * keeps it.
 *
 * @param {Array<{x:number, y?:number, z:number}>} corners - polygonCorners
 * @returns {[object, object] | null} the two end corners (as stored), or null
 *   with fewer than two corners or no length in plan
 */

// Corners closer than this in plan stand at the same end. The corpus keeps
// every outline corner within 1 cm of its wall line.
const END_TOLERANCE_M = 0.01;

function planDistanceSq(a, b) {
  return (a.x - b.x) ** 2 + (a.z - b.z) ** 2;
}

/** Index of the lowest corner standing within tolerance of `corners[at]`. */
function lowestAt(corners, at) {
  let best = at;
  for (let k = 0; k < corners.length; k++) {
    if (
      planDistanceSq(corners[k], corners[at]) <= END_TOLERANCE_M ** 2 &&
      (corners[k].y || 0) < (corners[best].y || 0)
    ) {
      best = k;
    }
  }
  return best;
}

function wallOutlineEnds(corners) {
  if (!Array.isArray(corners) || corners.length < 2) return null;

  let farthest = 0;
  let pair = null;
  for (let i = 0; i < corners.length; i++) {
    for (let j = i + 1; j < corners.length; j++) {
      const d = planDistanceSq(corners[i], corners[j]);
      if (d > farthest) {
        farthest = d;
        pair = [i, j];
      }
    }
  }
  if (!pair) return null;

  const a = lowestAt(corners, pair[0]);
  const b = lowestAt(corners, pair[1]);
  return a < b ? [corners[a], corners[b]] : [corners[b], corners[a]];
}

module.exports = { wallOutlineEnds, END_TOLERANCE_M };
